// 任务耗时统计（业主 2026-10-11 要求）：安排后多久开始跑、跑了多久完成；执行期再拆「AI 在跑」与「等人」
// 全部从任务时间线的 status:<状态> 事件推算，不额外存字段

export interface TaskTiming {
  /** 第一次入队（被安排）时间 */
  queuedAt: string | null;
  /** 第一次开始执行时间 */
  startedAt: string | null;
  /** 结束时间（完成/失败/取消） */
  endedAt: string | null;
  /** 入队 → 开始执行（毫秒） */
  waitMs: number | null;
  /** 开始执行 → 结束（未结束则到现在） */
  runMs: number | null;
  /** 执行期内 AI 在跑的时间（running） */
  aiMs: number | null;
  /** 执行期内等人的时间（等扫码 / 等审核 / 卡住） */
  humanMs: number | null;
  /** 还在进行中（runMs 会继续增长） */
  inProgress: boolean;
}

const HUMAN = new Set(["waiting_scan", "review_required", "blocked"]);
const END = new Set(["done", "failed", "cancelled"]);

export function computeTiming(timeline: { at: string | Date; kind: string }[], now: Date = new Date()): TaskTiming {
  const ev = timeline
    .filter((e) => e.kind.startsWith("status:"))
    .map((e) => ({ t: new Date(e.at).getTime(), s: e.kind.slice(7) }))
    .sort((a, b) => a.t - b.t);
  const q = ev.find((e) => e.s === "queued");
  const r = ev.find((e) => e.s === "running");
  const end = r ? ev.find((e) => e.t >= r.t && END.has(e.s)) : undefined;
  let ai = 0;
  let human = 0;
  if (r) {
    const stop = end ? end.t : now.getTime();
    const seq = ev.filter((e) => e.t >= r.t && e.t <= stop);
    for (let i = 0; i < seq.length; i++) {
      const cur = seq[i] as { t: number; s: string };
      const next = i + 1 < seq.length ? (seq[i + 1] as { t: number }).t : stop;
      const span = Math.max(0, next - cur.t);
      if (cur.s === "running") ai += span;
      else if (HUMAN.has(cur.s)) human += span;
    }
  }
  const iso = (t?: number) => (t === undefined ? null : new Date(t).toISOString());
  return {
    queuedAt: iso(q?.t),
    startedAt: iso(r?.t),
    endedAt: iso(end?.t),
    waitMs: q && r ? Math.max(0, r.t - q.t) : q && !r ? Math.max(0, now.getTime() - q.t) : null,
    runMs: r ? Math.max(0, (end ? end.t : now.getTime()) - r.t) : null,
    aiMs: r ? ai : null,
    humanMs: r ? human : null,
    inProgress: !!(q && !end && !ev.some((e) => END.has(e.s))),
  };
}

/** 时长：45秒 / 3分12秒 / 1小时5分 / 2天3小时 */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || ms < 0) return "";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}秒`;
  const m = Math.floor(s / 60);
  if (m < 60) return s % 60 ? `${m}分${s % 60}秒` : `${m}分`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h}小时${m % 60}分` : `${h}小时`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d}天${h % 24}小时` : `${d}天`;
}
